import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from "axios";
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { CardCvcElement, CardExpiryElement, CardNumberElement } from "@stripe/react-stripe-js";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { InputField, SelectField, SwitchField, TextareaField } from 'component/form/Field';
import config from "../../../config";
import customToast from "component/Toastr";
import { BookingPaymentApi, BookingPaymentFailedApi, BookingStoreApi } from 'store/slices/bookingSlice';
import { swalErrorHtml } from "component/Sweatalert2";
import "assets/css/CardSectionStyles.css";
import moment from 'moment';
import authHeader from 'services/auth-header';
import BookingStep6 from './Step6';
import Swal from 'sweetalert2';

// eslint-disable-next-line react/prop-types
const BookingStep3 = ({ voucherFieldVisible, setActiveSection, paymentSummary, isBookingSelectedServicesData }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState({});
  const [succeeded, setSucceeded] = useState(false);
  const isBookingApi = useSelector((state) => state.booking.isBookingApi);
  const cancellationPolicies = isBookingApi?.salon?.cancellation_policy || "";
  const [bookingSuccess, setBookingSuccess] = useState(false); // State to track booking success
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherValid, setVoucherValid] = useState(false);
  const [voucherAmount, setVoucherAmount] = useState(0);
  const staticObjectData = useSelector((state) => state?.bookingSummary?.bookingSummaryData)

  const depositRequired = paymentSummary?.totals?.depositTotal > 0;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const amountToPay = depositRequired ?
    paymentSummary.totals.depositTotal :
    0;

  // Add these new state variables
  const [voucherDetails, setVoucherDetails] = useState({
    voucher_code: '',
    voucher_discount: '',
    voucher_id: null,
    voucher_to_id: null,
    voucher_type: ''
  });
  // const isBookingSelectedServicesData = props.isBookingSelectedServicesData;
  const objectData = isBookingSelectedServicesData.filter(item => {
    if (item.categories) {
      return item.categories.some(category =>
        category.treatments.some(treatment =>
          category.category_id && treatment.treatment_id && treatment.staff_id
        )
      );
    }
    return false;
  }).map(item => {
    return item.categories.flatMap(category => {
      return category.treatments.map(treatment => {


        // Extract date and time components
        const dateFromDateof = (treatment.formattedDateTime).split(' ')[0];
        const timeFromDateTime = (treatment.selectedTime);
        const combinedDateTime = `${dateFromDateof} ${timeFromDateTime}`;

        return {
          id: treatment.treatment_id,
          category_id: category.category_id,
          service_id: treatment.treatment_id,
          staff_id: treatment.staff_id,
          serviceObjData: {
            id: treatment.treatment_id,
            resource_id: null,
            salon_id: isBookingApi?.salon?.id || '',
            category_id: category.category_id,
            tax_id: null,
            position: treatment.position || 0,
            name: treatment.treatment_name,
            description: treatment.description,
            duration: treatment.duration || "60",
            padding_time: treatment.padding_time || "15",
            color: "",
            service_booked_online: 1,
            deposit_booked_online: treatment.deposit_booked_online || 0,
            deposit_booked_price: item.totals.depositTotal || 0,
            hide_service_booking_widget: 0,
            voucher_online: treatment.voucher_online || 0,
            isNotId: 0
          },
          staffObjData: {
            id: treatment.staff_id,
            salon_id: isBookingApi?.salon?.id || '',
            price_tier_id: treatment.price_tier_id || null,
            first_name: treatment.staff_name.split(' ')[0],
            last_name: treatment.staff_name.split(' ')[1] || '',
            email: treatment.staff_email || '',
            phone_number: treatment.staff_phone || '',
            profile_photo: treatment.staff_profile_photo || null,
            address: treatment.staff_address || null,
            street: treatment.staff_street || null,
            suburb: treatment.staff_suburb || null,
            state: treatment.staff_state || null,
            postcode: treatment.staff_postcode || null,
            calendar_booking: 1,
            is_hide_in_online_booking: 0,
            profile_photo_url: treatment.staff_profile_photo_url || "",
            OnlineBookings: 0,
            check_available_time: {
              time: [],
              resource_item: [],
              staff: []
            },
            TotalClientAppointment: 0,
            TotalFutureAppointment: 0,
            retention: 0,
            pricetier: {
              id: treatment.price_tier_id || null,
              name: treatment.price_tier_name || 'General',
              totalStaff: treatment.total_staff || 0
            },
            staffservicesprice: {
              id: treatment.staffservicesprice_id || null,
              price: treatment.staffservicesprice_price || "0.00",
              add_on_price: treatment.staffservicesprice_add_on_price || "0.00",
              is_active: treatment.staffservicesprice_is_active || 1,
              is_active_at: treatment.staffservicesprice_is_active_at || null,
              created_at: treatment.staffservicesprice_created_at || null,
              updated_at: treatment.staffservicesprice_updated_at || null,
              add_on_services_count: treatment.staffservicesprice_add_on_services_count || 0
            }
          },
          datetime: combinedDateTime, // Using the combined datetime here
          totalprice: parseFloat(treatment.price) || 0,
          duration: treatment.duration || "01:00",
          padding_time: treatment.padding_time || "00:15",
          durationWithPadding: "01:15",
          parentAppointmentEndTime: "",
          resource_item_id: "",
          resource_name: "",
          dateof: treatment.formattedDateTime,
        };
      });
    });
  }).flat();
  // Use the calculated amounts in your render
  const calculatePaymentAmounts = () => {
    const totalBookingCost = paymentSummary?.totals?.total || 0;
    const depositAmount = amountToPay || 0;

    // Limit voucher amount to not exceed total booking cost
    let effectiveVoucherAmount = 0;
    if (voucherValid) {
      // Take the smaller value between voucherAmount and totalBookingCost
      effectiveVoucherAmount = Math.min(voucherAmount, totalBookingCost);
    }

    // Calculate remaining amount to pay in salon
    let remainingAmount = totalBookingCost - effectiveVoucherAmount;

    // Calculate deposit after voucher
    let finalDepositAmount = depositAmount;
    if (voucherValid) {
      // Reduce deposit by voucher amount, but don't go below 0
      finalDepositAmount = Math.max(0, depositAmount - effectiveVoucherAmount);
    }

    // Ensure remaining amount doesn't go below 0
    remainingAmount = Math.max(0, remainingAmount - finalDepositAmount);

    // Determine if deposit is still required after voucher
    const finalDepositRequired = finalDepositAmount > 0;

    return {
      totalBookingCost,
      depositAmount: finalDepositAmount,
      remainingAmount,
      requiresDeposit: finalDepositRequired,
      appliedVoucherAmount: effectiveVoucherAmount // Add this to show how much of the voucher was actually applied
    };
  };

  const paymentDetails = calculatePaymentAmounts();
  // Calculate deposit amount
  // Country codes setup
  const countryCodes = config.country_codes.map((code) => ({
    value: code.code,
    label: `${code.flag} ${code.country_name} (${code.code})`
  }));

  const initialValues = {
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    country_code: config.default_country_code,
    description: '',
    cancellation_policy: '',
    emailnotification: '',
    uid: isBookingApi?.uid || '',
    salon_id: isBookingApi?.salon?.id || '',
    bookingStepName: 'BookServices',
  };
  const validationSchema = Yup.object({
    first_name: Yup.string().trim().max(100).label(t("First Name")).required(),
    last_name: Yup.string().trim().max(100).label(t("Last Name")).required(),
    email: Yup.string().trim().max(200).email().label(t("Email Address")).required(),
    cancellation_policy: Yup.bool().required(t("please accept terms and conditions")),
    phone_number: Yup.string()
      .trim()
      .matches(config.phone_number_pattern, t(config.phone_number_433_error))
      .label(t("Mobile"))
      .required(),
    // country_code: Yup.string().trim().label(t("Country code")).required(),
    description: Yup.string().trim().label(t("Comments")),
    uid: Yup.string().required('The uid field is required'),
    salon_id: Yup.string().required('The salon id field is required'),
    bookingStepName: Yup.string().required('The booking step name field is required'),
  });
  const validateVoucher = async () => {
    try {
      if (voucherCode.trim() === "") {
        customToast.error("Please enter voucher code");
        return;
      }
      // Check if a voucher is already applied and if it's the same code
      if (voucherValid && voucherDetails.voucher_code.toLowerCase() === voucherCode.toLowerCase()) {
        customToast.error("This voucher code has already been applied");
        setVoucherCode(""); // Clear the input field
        return;
      }
      const serviceIds = staticObjectData?.data?.map(item => item.service_id);


      const response = await axios.post(`${config.API_URL}widget/booking/validate-voucher`, {
        code: voucherCode,
        salon_id: isBookingApi?.salon?.id,
        services_id: serviceIds,
      }, {
        headers: authHeader(),
      });

      if (response?.status === 200) {
        const voucherData = response.data.data;
        setVoucherValid(true);
        setVoucherAmount(parseFloat(voucherData.voucher_amount));
        // Store all voucher details
        setVoucherDetails({
          voucher_code: voucherCode,
          // voucher_discount: voucherData.voucher_amount,
          voucher_id: voucherData.voucher_id,
          voucher_to_id: voucherData.voucher_to_id,
          voucher_type: voucherData.voucher_type
        });
        setVoucherCode("");
        customToast.success(response.data.message);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      setVoucherValid(false);
      setVoucherAmount(0);
      setVoucherDetails({
        voucher_code: '',
        voucher_discount: '',
        voucher_id: null,
        voucher_to_id: null,
        voucher_type: ''
      });
      if (error?.response?.status == "422") {
        customToast.error(error?.response?.data?.errors);
      } else {
        customToast.error(error?.response?.data?.message);
      }

    }
  };

  const handleSubmit = async (values, { setErrors, setSubmitting }) => {
    setLoading(true);
    try {
      const paymentDetails = calculatePaymentAmounts();

      // Prepare base booking data
      const baseBookingData = {
        ...values,
        uid: values.uid,
        salon_id: values.salon_id,
        bookingStepName: values.bookingStepName,
        BookingSelectedServicesData: staticObjectData?.data,
        totaldeposit: paymentDetails.depositAmount,
      };

      // Add voucher details if valid
      if (voucherValid) {
        Object.assign(baseBookingData, {
          voucher_code: voucherDetails.voucher_code,
          voucher_discount: paymentDetails.appliedVoucherAmount.toFixed(2),
          voucher_id: voucherDetails.voucher_id,
          voucher_to_id: voucherDetails.voucher_to_id,
          voucher_type: voucherDetails.voucher_type
        });
      }

      // Handle payment required booking
      if (paymentDetails.requiresDeposit) {
        if (!stripe || !elements) {
          setError("Stripe has not been properly initialized");
          setLoading(false);
          return;
        }

        try {
          const { token, error: stripeError } = await stripe.createToken(
            elements.getElement(CardNumberElement)
          );

          if (stripeError) {
            setError(stripeError.message);
            setLoading(false);
            return;
          }

          // Add stripe token to booking data
          const bookingData = {
            ...baseBookingData,
            stripe_token_id: token.id
          };

          // Process booking with payment
          const action = await dispatch(BookingStoreApi(bookingData));

          if (action.meta.requestStatus === "fulfilled") {
            const bookingstoredata = action.payload;
            const { clientSecret, stripe_payment_method_id, type = "" } = bookingstoredata;

            if (clientSecret) {
              const paymentResult = await stripe.confirmCardPayment(clientSecret, {
                payment_method: stripe_payment_method_id,
              });

              if (paymentResult.error) {
                setError(`Payment failed: ${paymentResult.error.message}`);
                setLoading(false);
                setStatus({ error: true });
                await dispatch(BookingPaymentFailedApi({ ...bookingstoredata, error: paymentResult.error }));
                return;
              }

              const { paymentIntent } = paymentResult;

              if (paymentIntent?.id && paymentIntent?.status === "succeeded") {
                const paymentData = {
                  ...bookingstoredata,
                  paymentIntent: {
                    id: paymentIntent.id,
                    status: paymentIntent.status
                  },
                  type
                };

                const paymentAction = await dispatch(BookingPaymentApi(paymentData));

                if (paymentAction.meta.requestStatus === "fulfilled") {
                  handleBookingSuccess();
                } else {
                  handlePaymentError(paymentAction, action, setErrors);
                  setLoading(false);
                  setSubmitting(false);
                }
              }
            } else {
              // Handle successful booking without payment confirmation
              handleBookingSuccess();
            }
          } else {
            handleBookingError(action, setErrors);
            setLoading(false);
            setSubmitting(false);
          }
        } catch (stripeError) {
          setError(`Payment processing error: ${stripeError.message}`);
          setLoading(false);
          setSubmitting(false);
        }
      } else {
        // Handle booking without payment
        try {
          const action = await dispatch(BookingStoreApi(baseBookingData));

          if (action.meta.requestStatus === "fulfilled") {
            handleBookingSuccess();
          } else {
            handleBookingError(action, setErrors);
            setLoading(false);
            setSubmitting(false);
          }
        } catch (error) {
          setError(`Booking error: ${error.message}`);
          setLoading(false);
          setSubmitting(false);
        }
      }
    } catch (error) {
      setError(`General error: ${error.message}`);
      setStatus({ success: false });
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Helper function to handle successful booking
  const handleBookingSuccess = () => {
    setStatus({ success: true });
    setLoading(false);
    setSucceeded(true);
    customToast.success(t("Appointment Booking Successfully."));
    setBookingSuccess(true);
  };

  // Update the error handlers to not use setStatus
  const handlePaymentError = (actionpayment, action, setErrors) => {
    const status = actionpayment.payload?.status;
    const errors = actionpayment.payload?.message?.errors;
    const response = action.payload?.message;

    if (status === 422) {
      const NotifyContent = () => (
        <>
          <p className="mb-2 text-danger salert text-justify">{response?.message}</p>
          <ul className="list-unstyled">
            {errors &&
              Object.keys(errors).map((a, n) => {
                if (errors[a].length > 0) {
                  return (
                    <React.Fragment key={n}>
                      {errors[a].map((as, ns) => (
                        <li key={`${n}-${ns}`} className="text-danger salert form-text text-start">
                          {as}
                        </li>
                      ))}
                    </React.Fragment>
                  );
                }
                return null;
              })}
          </ul>
        </>
      );
      swalErrorHtml({ title: "Warning", html: <NotifyContent />, icon: "error" });
      setErrors(errors);
    }
  };

  const handleBookingError = (action, setErrors) => {
    const status = action.payload?.status;
    const errors = action.payload?.message?.errors;
    const response = action.payload?.message;

    if (status === 422) {
      const NotifyContent = () => (
        <>
          <p className="mb-2 text-danger salert text-justify">{response?.message}</p>
          <ul className="list-unstyled">
            {errors &&
              Object.keys(errors).map((a, n) => {
                if (errors[a].length > 0) {
                  return (
                    <React.Fragment key={n}>
                      {errors[a].map((as, ns) => (
                        <li key={`${n}-${ns}`} className="text-danger salert form-text text-start">
                          {as}
                        </li>
                      ))}
                    </React.Fragment>
                  );
                }
                return null;
              })}
          </ul>
        </>
      );
      swalErrorHtml({ title: "Warning", html: <NotifyContent />, icon: "error" });
      setErrors(errors);
    } else if (status === 410) {
      // Custom styled alert for time slot no longer available
      Swal.fire({
        title: 'Oops!',
        html: `
          <div style="font-family: 'Your Brand Font', sans-serif;">
            <p style="color: #333; font-size: 1.1em; margin-bottom: 1em;">
             Oops, looks like someone else got in before you, this appointment time has now been taken.
            </p>
            <p style="color: #666; font-size: 1em;">
             Please go back and choose a different time.
            </p>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'Choose Another Time',
        confirmButtonColor: '#FF6B6B', // Your brand primary color
        background: '#fff',
        customClass: {
          popup: 'custom-popup-class',
          title: 'custom-title-class',
          confirmButton: 'custom-confirm-button'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          setActiveSection('services'); // Go back to services section
        }
      });
      setStatus({ warning: response?.message, booked: response?.booked });
    }
  };
  if (bookingSuccess) {
    return <BookingStep6 />; // Render BookingStep6 component
  }
  return (
    <>
      <div className="d-flex align-items-center mb-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setActiveSection('services')}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Services
        </button>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <form id="payment-form" noValidate onSubmit={formik.handleSubmit}>
            <div className="confirm-from-cover">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <InputField
                    type="text"
                    name="first_name"
                    label={t("First Name") + "*"}
                    controlId="bookingForm-first_name"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <InputField
                    type="text"
                    name="last_name"
                    label={t("Last Name") + "*"}
                    controlId="bookingForm-last_name"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <InputField
                    type="text"
                    name="phone_number"
                    mask={config.phone_number_mask}
                    label={t("Mobile") + "*"}
                    controlId="bookingForm-phone_number"
                  />
                </div>
                <div className="col-md-6 mb-3">

                  <InputField
                    type="text"
                    name="email"
                    label={t("Email Address") + "*"}
                    controlId="bookingForm-email"
                  />
                </div>

              </div>

              {/* <div className="col-md-12 mb-3">
                <InputField
                  type="text"
                  name="email"
                  label={t("Email Address") + "*"}
                  controlId="bookingForm-email"
                />
              </div> */}

              <div className="col-md-12 mb-4">
                <TextareaField
                  name="description"
                  placeholder={t("For example, allergic to latex")}
                  label={t("Comments")}
                  controlId="bookingForm-description"
                />
              </div>
              <div className="services-summary">
                <h2>Services Summary</h2>
                <div className="scrollable-summary">
                  {staticObjectData.data.map((item, index) => {
                    // Correctly placed console.log statement


                    // Correct return statement with JSX
                    return (
                      <div key={index} className="service-item" style={{
                        backgroundColor: '#FFF1EE',
                        padding: '10px',
                        borderRadius: '8px',
                        marginBottom: '10px'
                      }}>
                        <div className="service-details" style={{ display: 'flex', alignItems: 'center' }}>
                          <div className="service-icon">
                            <img src={config.imagepath + "serviceicon.svg"} alt="" className="rounded-circle wh-36" />
                          </div>
                          <div>
                            <div className="service-name" style={{ fontWeight: 'bold' }}>
                              {`${item.serviceObjData.name} with ${item?.noPref ? "No Preference" : `${item.staffObjData.first_name} ${item.staffObjData.last_name}`}`}
                            </div>
                            <div className="service-info" style={{ fontSize: '0.9em', color: '#666' }}>
                              on {moment(item.datetime).format('dddd MMMM DD, YYYY')} at {moment(item.datetime).format('HH:mm')}
                            </div>
                            <div className="service-duration" style={{ fontSize: '0.9em', color: '#666' }}>
                              Duration: {item.duration}
                            </div>
                            {item.serviceObjData.deposit_booked_online === 1 && (
                              <div className="service-deposit" style={{ fontSize: '0.9em', color: '#666' }}>
                                Deposit Required: ${item.serviceObjData.deposit_booked_price}
                              </div>
                            )}
                            {item.serviceObjData.serviceprice && item.serviceObjData.serviceprice[0] && (
                              <div className="service-price" style={{ fontSize: '0.9em', color: '#666' }}>
                                Price: ${item.serviceObjData.serviceprice[0].price}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {true && (
                <div className="voucher-section d-flex">
                  <input
                    type="text"
                    placeholder="Enter voucher code for discounts"
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value)}
                    className="form-control me-2" // Bootstrap class for styling
                    style={{ flex: 1 }} // Make input take available space
                  />
                  <button
                    type="button"
                    className="apply-btn btn btn-success"
                    onClick={validateVoucher}
                  >
                    Apply
                  </button>
                </div>
              )}

              <div className="payment-summary">
                <h2>Payment Summary</h2>
                <div className="summary-row">
                  <span>Total Booking Cost</span>
                  <span>${paymentDetails.totalBookingCost.toFixed(2)}</span>
                </div>

                {voucherValid && (
                  <div className="summary-row">
                    <span>Voucher Applied {voucherAmount > paymentDetails.totalBookingCost &&
                      `(Limited to booking total)`}</span>
                    <span>-${paymentDetails.appliedVoucherAmount.toFixed(2)}</span>
                  </div>
                )}
                {/* 
                {paymentDetails.requiresDeposit && ( */}
                <div className="summary-row">
                  <span>Deposit to Pay Today</span>
                  <span>${paymentDetails.depositAmount.toFixed(2)}</span>
                </div>
                {/* )} */}

                <div className="summary-row total">
                  <span>Total to Pay in Salon</span>
                  <span>${paymentDetails.remainingAmount.toFixed(2)}</span>
                </div>
              </div>
              {paymentDetails.requiresDeposit && (
                <>
                  <div className="bookin-mess col-12">
                    <h3> {t("A deposit of AUD {{ price }} is required to complete your booking.", {
                      price: "$" + paymentDetails.depositAmount.toFixed(2)
                    })}</h3>
                  </div>

                  <div className="row">
                    <div className="col-12 mb-3">
                      <h4 htmlFor="" className="fw-600">Card Details</h4>
                      <label htmlFor="card-number">{t("Card Number")}</label>
                      <CardNumberElement
                        id="card-number"
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#32325d',
                              fontFamily: 'Arial, sans-serif',
                              '::placeholder': {
                                color: '#aab7c4',
                              },
                            },
                            invalid: {
                              color: '#fa755a',
                            },
                          },
                        }}
                      />
                    </div>
                    <div className="col-6 mb-3">
                      <label htmlFor="card-expiry">{t("Expiration Date")}</label>
                      <CardExpiryElement
                        id="card-expiry"
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#32325d',
                              fontFamily: 'Arial, sans-serif',
                              '::placeholder': {
                                color: '#aab7c4',
                              },
                            },
                            invalid: {
                              color: '#fa755a',
                            },
                          },
                        }}
                      />
                    </div>
                    <div className="col-6 mb-3">
                      <label htmlFor="card-cvc">{t("CVC")}</label>
                      <CardCvcElement
                        id="card-cvc"
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#32325d',
                              fontFamily: 'Arial, sans-serif',
                              '::placeholder': {
                                color: '#aab7c4',
                              },
                            },
                            invalid: {
                              color: '#fa755a',
                            },
                          },
                        }}
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="card-error invalid-feedback d-block mb-4" role="alert">
                      {error}
                    </div>
                  )}
                </>
              )}

              <div className="col-md-12 mb-4">
                <div className="cancel-policy mb-3">
                  <h4 className="fw-semibold mb-2">{t("Cancellation Policy")}</h4>
                  <p className="mb-3 h6 text-justify">{t(cancellationPolicies)}</p>
                  <SwitchField
                    name="cancellation_policy"
                    label={t("I agree to the cancellation policy")}
                    controlId="bookingForm-cancellation_policy"
                    value="1"
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        // setTimeout(() => {
                        formik.setFieldValue("cancellation_policy", 1, false);
                        // }, 100);
                      } else {
                        // setTimeout(() => {
                        formik.setFieldValue("cancellation_policy", "", false);
                        // }, 100);
                      }
                      //formik.handleChange(e);
                    }}
                  />
                  {formik && formik.errors && formik.touched.cancellation_policy && formik.errors.cancellation_policy && (
                    <span className="text-danger">{formik.errors.cancellation_policy}</span>
                  )}
                </div>

                <div className="ps-3">
                  <SwitchField
                    name="emailnotification"
                    label={t("I want to receive emails with latest news, offers and updates.")}
                    controlId="bookingForm-emailnotification"
                    value="1"
                    onChange={(e) => {
                      formik.setFieldValue("emailnotification", e.currentTarget.checked ? 1 : "", false);
                    }}
                  />
                </div>
              </div>

              <div className="col-md-12">
                <button
                  type="submit"
                  className="btn btn-primary w-100 btn-lg"
                  disabled={loading}
                >
                  {loading && <span className="spinner-border spinner-border-sm me-2"></span>}
                  {depositRequired ? t("Pay deposit and book") : t("Book Now")}
                </button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

BookingStep3.propTypes = {
  setActiveSection: PropTypes.func.isRequired,
  paymentSummary: PropTypes.shape({
    selectedItems: PropTypes.arrayOf(PropTypes.shape({
      treatmentName: PropTypes.string.isRequired,
      staffName: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      selectedTime: PropTypes.string,
      selectedDate: PropTypes.string
    })).isRequired,
    totals: PropTypes.object.isRequired
  }).isRequired,
  isBookingSelectedServicesData: PropTypes.array.isRequired
};

export default BookingStep3;